import { cn } from '../../lib/utils';
import { Card } from './Card';

/**
 * Uniwersalny KPI tile / metric card.
 * Zastępuje: Kpi (HistoryPage), KpiTile (NewOrderPage QueueProgress), HeroStat (LiveDashboard).
 *
 * Props:
 *  - label:    string — uppercase mała etykieta na górze
 *  - value:    string | number | ReactNode — duża cyfra
 *  - sub:      string — opis pod cyfrą
 *  - icon:     ReactNode — ikona w right-top corner (lucide)
 *  - tone:     'neutral' | 'brand' | 'success' | 'danger' | 'warning' | 'info'
 *  - dot:      bool — live-dot przy cyfrze (realtime hint)
 *  - trend:    { value: number, label?: string } — np. {value: 12, label: '7d'} → +12% / -3%
 *  - emphasis: 'default' | 'gradient' — gradient daje brand text dla key numbers
 *  - small:    bool — wartość w mniejszym foncie (dla email / wide string content)
 *  - className
 */
const TONE_BG = {
  neutral: 'text-ink-500 bg-ink-100 dark:text-ink-400 dark:bg-ink-800',
  brand:   'text-brand-700 bg-brand-50 dark:text-brand-300 dark:bg-brand-900/40',
  success: 'text-success-dark bg-success-light dark:text-success dark:bg-success/15',
  danger:  'text-danger-dark bg-danger-light dark:text-danger dark:bg-danger/15',
  warning: 'text-warning-dark bg-warning-light dark:text-warning dark:bg-warning/15',
  info:    'text-info-dark bg-info-light dark:text-info dark:bg-info/15',
};

export default function MetricCard({
  label,
  value,
  sub,
  icon,
  tone = 'neutral',
  dot = false,
  trend,
  emphasis = 'default',
  small = false,
  className,
}) {
  const valueClasses = cn(
    'font-bold tracking-tighter break-words leading-none',
    small ? 'text-sm' : 'text-2xl',
    emphasis === 'gradient'
      ? 'bg-gradient-to-br from-brand-600 to-brand-900 bg-clip-text text-transparent dark:from-brand-300 dark:to-brand-500'
      : 'text-ink-900 dark:text-ink-50',
  );

  const trendPositive = trend && trend.value >= 0;

  return (
    <Card className={cn('p-4', className)}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-2xs uppercase tracking-wide font-bold text-ink-500 dark:text-ink-400 leading-tight">
          {label}
        </div>
        {icon && (
          <div className={cn('w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0', TONE_BG[tone])}>
            {icon}
          </div>
        )}
      </div>
      <div className="mt-2 flex items-baseline gap-2 flex-wrap">
        <div className={valueClasses}>
          {value ?? '—'}
        </div>
        {dot && <span className="live-dot" />}
        {trend && (
          <span className={cn(
            'inline-flex items-center text-xs font-semibold tabular-nums',
            trendPositive ? 'text-success-dark dark:text-success' : 'text-danger-dark dark:text-danger',
          )}>
            {trendPositive ? '+' : ''}{trend.value}%
            {trend.label && <span className="text-ink-400 dark:text-ink-500 ml-1 font-normal">{trend.label}</span>}
          </span>
        )}
      </div>
      {sub && (
        <div className="text-2xs text-ink-500 dark:text-ink-400 mt-1">{sub}</div>
      )}
    </Card>
  );
}

/**
 * Inline stat (bez Card wrappera) — dla użycia wewnątrz innego Card (np. hero LiveDashboard).
 */
export function InlineStat({ label, value, className }) {
  return (
    <div className={className}>
      <div className="text-2xs uppercase font-bold tracking-wide text-ink-400 dark:text-ink-500 mb-0.5">
        {label}
      </div>
      <div className="text-xl font-bold tabular-nums tracking-tighter text-ink-900 dark:text-ink-50 leading-none">
        {value}
      </div>
    </div>
  );
}
